import React, { useEffect, useMemo, useState } from 'react';
import { TreeSelect, Spin, Typography } from 'antd';
import api from '../../services/api';

const { Text } = Typography;

function normalizeToArray(values) {
  if (values == null || values === '') return [];
  return Array.isArray(values) ? values : [values];
}

function filterUserIds(values) {
  return normalizeToArray(values).filter(
    (value) => value != null && value !== '' && !String(value).startsWith('org:')
  );
}

export function buildUserMapFromAssigneeTree(treeData) {
  const map = {};
  const walk = (nodes) => {
    (nodes || []).forEach((node) => {
      const value = node.value != null ? String(node.value) : '';
      if (value && !value.startsWith('org:')) {
        map[value] = node.title;
      }
      if (node.children?.length) walk(node.children);
    });
  };
  walk(treeData);
  return map;
}

export function useWfStepAssigneeTree() {
  const [treeData, setTreeData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const res = await api.get('/approval-center/wf-step-assignee-tree');
        if (!cancelled && res.data.success) {
          setTreeData(res.data.data || []);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e.response?.data?.message || '加载职能组织人员失败');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const userMap = useMemo(() => buildUserMapFromAssigneeTree(treeData), [treeData]);

  return { treeData, userMap, loading, error };
}

export function WfStepAssigneeTreeSelect({
  treeData,
  loading,
  value = [],
  onChange,
  multiple = true,
  disabled = false,
  placeholder = '展开职能组织并选择人员'
}) {
  if (loading) {
    return <div style={{ padding: '8px 0', textAlign: 'center' }}><Spin size="small" /></div>;
  }

  const selectedIds = filterUserIds(value);
  const treeValue = multiple ? selectedIds : selectedIds[0];

  return (
    <TreeSelect
      treeData={treeData}
      value={treeValue}
      onChange={(vals) => onChange?.(filterUserIds(vals))}
      multiple={multiple}
      maxCount={multiple ? undefined : 1}
      treeDefaultExpandAll
      showSearch
      treeNodeFilterProp="title"
      placeholder={placeholder}
      disabled={disabled || !treeData.length}
      style={{ width: '100%' }}
      dropdownStyle={{ maxHeight: 360, overflow: 'auto' }}
    />
  );
}

export function validateWfStepAssignees(steps, value = {}) {
  for (const step of steps) {
    const selected = value[step.step_id] || [];
    if (!selected.length) {
      return `请选择「${step.label}」的${step.type === 'cc' ? '抄送人' : '审批人'}`;
    }
  }
  return null;
}

export function mergeWfStepAssigneesIntoPayload(payload, wfStepAssignees) {
  if (!wfStepAssignees || !Object.keys(wfStepAssignees).length) return payload;
  return {
    ...payload,
    wf_step_assignees: wfStepAssignees,
    form_data: {
      ...(payload?.form_data || {}),
      wf_step_assignees: wfStepAssignees
    }
  };
}

/** @deprecated 保留兼容；请使用时间线内嵌 AssigneePickNode */
export default function WorkflowStepAssigneePicker({
  steps = [],
  value = {},
  onChange,
  disabled = false
}) {
  const { treeData, loading, error } = useWfStepAssigneeTree();

  if (!steps.length) return null;

  const handleStepChange = (stepId, nextValues) => {
    onChange?.({
      ...value,
      [stepId]: filterUserIds(nextValues)
    });
  };

  return (
    <div className="wf-step-assignee-picker" style={{ marginBottom: 16 }}>
      {error && <Text type="danger">{error}</Text>}
      {steps.map((step) => {
        const isSequentialApprover = step.type === 'approver' && step.multi_mode === 'sequential';
        return (
          <div key={step.step_id} style={{ marginBottom: 12 }}>
            <WfStepAssigneeTreeSelect
              treeData={treeData}
              loading={loading}
              value={value[step.step_id] || []}
              onChange={(vals) => handleStepChange(step.step_id, vals)}
              multiple={!isSequentialApprover}
              disabled={disabled}
            />
          </div>
        );
      })}
    </div>
  );
}
